//Adding new scopes here will add them to user management in the organisation flow
//Frontend converts from these to provide options upon user creation.
// super becomes  SUPER (title for scope) and SUPER_DESCRIPTION (description for scope). These both need to be added to relevant translation files.

export const availableUserScopes = ['super', 'crm', 'customers', 'triggers', 'users', 'payments'] as const;
export type AvailableUserScopes = (typeof availableUserScopes)[number];

// One map for menu visibility, route guards and server middleware.
export const featureScopes = {
    customers: 'customers',
    campaigns: 'crm',
    triggers: 'triggers',
    users: 'users',
    funpay: 'payments',
    settings: 'super'
} as const satisfies Record<string, AvailableUserScopes>;

export type AppFeature = keyof typeof featureScopes;

export interface ScopeSubject {
    role?: string;
    organizationData?: { [orgId: string]: { scopes?: AvailableUserScopes[] } };
}

/** Superadmins pass everything; `super` passes everything in its org; otherwise the scope itself is required. */
export function userHasScope(user: ScopeSubject | null | undefined, orgId: string, required?: AvailableUserScopes): boolean {
    if (!user) {
        return false;
    }
    if (user.role === 'superadmin') {
        return true;
    }
    const orgData = user.organizationData?.[orgId];
    if (!orgData) {
        return false;
    }
    if (!required) {
        return true;
    }
    const scopes = orgData.scopes ?? [];
    return scopes.includes('super') || scopes.includes(required);
}

// Presets only fill in scopes; scopes remain the stored source of truth.
export const rolePresets = {
    OWNER: ['super'],
    MARKETING: ['crm', 'customers', 'triggers'],
    CUSTOMER_SERVICE: ['customers'],
    FINANCE: ['payments']
} as const satisfies Record<string, readonly AvailableUserScopes[]>;

export type RolePreset = keyof typeof rolePresets;

/** The preset whose scopes exactly match, or null for a custom combination. */
export function presetForScopes(scopes: readonly AvailableUserScopes[]): RolePreset | null {
    const wanted = [...new Set(scopes)].sort().join(',');
    for (const [preset, presetScopes] of Object.entries(rolePresets) as [RolePreset, readonly AvailableUserScopes[]][]) {
        if ([...presetScopes].sort().join(',') === wanted) {
            return preset;
        }
    }
    return null;
}

const scopesIn = (actor: ScopeSubject, orgId: string): AvailableUserScopes[] => actor.organizationData?.[orgId]?.scopes ?? [];

/** Superadmins, and holders of `super` in this org, may grant and manage anything in it. */
export function hasFullAccess(actor: ScopeSubject | null | undefined, orgId: string): boolean {
    return !!actor && (actor.role === 'superadmin' || scopesIn(actor, orgId).includes('super'));
}

/** Whether the actor may hand out this scope: only ones they hold, unless they have full access. */
export function canGrantScope(actor: ScopeSubject | null | undefined, orgId: string, scope: AvailableUserScopes): boolean {
    return !!actor && (hasFullAccess(actor, orgId) || scopesIn(actor, orgId).includes(scope));
}

/** An actor may only edit or remove users whose access is a subset of their own. */
export function canManageTarget(actor: ScopeSubject | null | undefined, orgId: string, targetScopes: readonly AvailableUserScopes[]): boolean {
    if (!actor) {
        return false;
    }
    if (hasFullAccess(actor, orgId)) {
        return true;
    }
    return !targetScopes.includes('super') && targetScopes.every((scope) => canGrantScope(actor, orgId, scope));
}
